'use client';

import React from 'react';
import classnames from 'classnames';
import Box from '../Box/Box';
import IconButton from '../IconButton/IconButton';
import Typography from '../Typography/Typography';
import styles from './StatusBanner.module.scss';

type StatusBannerType = 'success' | 'error' | 'warning' | 'info';
type StatusBannerTheme = 'dark' | 'light';

export type StatusBannerProps = {
  /** The semantic type of the banner — controls background color. */
  type: StatusBannerType;
  /** Solid backgrounds use white text. Light-theme info banners use a tinted background with dark text. Defaults to dark. */
  theme?: StatusBannerTheme;
  /** The message text to display. */
  message: string;
  /** Optional secondary line of text shown below the message. */
  subtitle?: string;
  /** Optional action element (e.g. a Button) rendered on the trailing edge. */
  action?: React.ReactNode;
  /** When set, renders an X button that calls this handler. */
  onClose?: () => void;
  /** Test ID for the root element. */
  dataTestId: string;
  /** Optional additional class name. */
  className?: string;
};

/**
 * Inline status feedback banner for displaying success or error messages after user actions.
 * Renders inline in the document flow — not a toast or modal.
 * @component
 * @param {StatusBannerProps} props
 * @returns {JSX.Element}
 * @example
 * <StatusBanner type="success" message="Item added to cart." dataTestId="cart-success" />
 * <StatusBanner type="error" message="Failed to add item." dataTestId="cart-error" />
 * <StatusBanner type="warning" message="Cart limit reached." dataTestId="cart-warning" />
 * <StatusBanner type="info" message="Matching happens after submission." dataTestId="cart-info" />
 */
const StatusBanner: React.FC<StatusBannerProps> = ({
  type,
  theme = 'dark',
  message,
  subtitle,
  action,
  onClose,
  dataTestId,
  className,
}) => {
  const isLight = theme === 'light';
  const isTinted = isLight && type === 'info';
  const hasTrailing = action != null || onClose != null;
  return (
    <Box
      pt={12}
      pb={12}
      pl={16}
      pr={16}
      data-testid={dataTestId}
      className={classnames(
        styles.root,
        styles[type],
        isTinted ? styles.infoLight : undefined,
        className
      )}
    >
      <Box className={classnames(styles.inner, hasTrailing ? styles.withAction : undefined)}>
        <Box className={styles.text}>
          <Typography variant="body" size="medium" color={isTinted ? 'black' : 'white'}>
            {message}
          </Typography>
          {subtitle && (
            <Typography variant="body" size="small" color={isTinted ? 'gray-3' : 'white'}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {hasTrailing && (
          <Box className={styles.action}>
            {action}
            {onClose && (
              <IconButton
                icon={{ category: 'global', name: 'close' }}
                size="small"
                theme={theme}
                buttonType="tertiary"
                className={styles.closeButton}
                gaComponent="status-banner-close"
                dataTestId={`${dataTestId}-close-button`}
                handleClick={onClose}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StatusBanner;
